import { ref, watchEffect, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { useModal, useToast } from 'vuestic-ui'
import apiClient, { getActiveToken } from '../../services/axios'
import api from '../../services/api'
import { CDNImage } from '../../services/media/DecryptImage'

const USERS_LIST_CACHE_STORAGE_KEY = 'users_tab_list_pages_cache_v2'
const USERS_LIST_CACHE_TTL_MS = 2 * 60 * 1000
const USERS_LIST_MEMORY_CACHE_MAX_ENTRIES = 32

type UsersListPageCacheEntry = { ts: number; items: any[]; total: number }

type FetchUsersOptions = {
  paginationChange?: boolean
}

const usersListPageMemoryCache = new Map<string, { items: any[]; total: number; ts: number }>()

// Shared list state — survives route remounts for faster pagination
const users = ref<any[]>([])
const isLoading = ref(false)
const hasLoadedUsers = ref(false)
const error = ref<any | null>(null)
const filters = ref({ isActive: true, search: '', id: '', display_id: '', phone: '', agentId: null as string | number | null })
const sorting = ref<{ sortBy: string; sortingOrder: 'asc' | 'desc' | null }>({ sortBy: 'username', sortingOrder: null })
const pagination = ref({ page: 1, perPage: 10, total: 0 })
const agents = ref<any[]>([])
const agentBaseCache = ref<{ key: string; items: any[] } | null>(null)
const agentFilterCache = ref<{ key: string; items: any[] } | null>(null)
const agentFilterUsesServerPagination = ref(false)
const fetchSequence = ref(0)
const avatarUrlCache = ref<Record<string, string>>({})

let fetchVipUsersPromise: Promise<void> | null = null
let activeUsersFetchController: AbortController | null = null
let pendingUsersTabPrefetch = false
let prefetchHandlers: {
  fetchUsers: () => Promise<void> | void
  fetchAgents: () => Promise<void> | void
} | null = null

const buildUsersListCacheKey = (
  params: any,
  role: string,
  isImpersonation: boolean,
  agentId = '',
) =>
  JSON.stringify({
    page: params.page,
    pageSize: params.pageSize,
    limit: params.limit,
    keyword: params.keyword || '',
    search: params.search || '',
    id: params.id || '',
    display_id: params.display_id || '',
    phone: params.phone || '',
    status: params.status || '',
    role,
    isImpersonation,
    agentId: agentId || '',
  })

const readUsersListPagesCacheStore = (): Record<string, UsersListPageCacheEntry> => {
  try {
    const raw = sessionStorage.getItem(USERS_LIST_CACHE_STORAGE_KEY)
    if (!raw) return {}

    const parsed = JSON.parse(raw) as Record<string, UsersListPageCacheEntry>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const writeUsersListPagesCacheStore = (store: Record<string, UsersListPageCacheEntry>) => {
  try {
    sessionStorage.setItem(USERS_LIST_CACHE_STORAGE_KEY, JSON.stringify(store))
  } catch {
    // Ignore storage quota errors
  }
}

const pruneUsersListPagesCacheStore = (
  store: Record<string, UsersListPageCacheEntry>,
  maxEntries: number,
) => {
  const keys = Object.keys(store)
  if (keys.length <= maxEntries) return store

  keys
    .sort((a, b) => (store[a]?.ts || 0) - (store[b]?.ts || 0))
    .slice(0, keys.length - maxEntries)
    .forEach((key) => {
      delete store[key]
    })

  return store
}

const readUsersListCacheFromStorage = (cacheKey: string): { items: any[]; total: number } | null => {
  try {
    const store = readUsersListPagesCacheStore()
    const entry = store[cacheKey]
    if (!entry || !Array.isArray(entry.items) || typeof entry.ts !== 'number') return null
    if (Date.now() - entry.ts > USERS_LIST_CACHE_TTL_MS) return null

    return {
      items: entry.items,
      total: Number(entry.total) || entry.items.length,
    }
  } catch {
    return null
  }
}

const writeUsersListCacheToStorage = (cacheKey: string, items: any[], total: number) => {
  try {
    const store = readUsersListPagesCacheStore()
    store[cacheKey] = { ts: Date.now(), items, total }
    writeUsersListPagesCacheStore(pruneUsersListPagesCacheStore(store, USERS_LIST_MEMORY_CACHE_MAX_ENTRIES))
  } catch {
    // Ignore storage quota errors
  }
}

/** Précharge le chunk et la liste utilisateurs avant l'ouverture de la page. */
export function prefetchUsersTab() {
  if (!getActiveToken()) return

  void import('./Users_Tab.vue')

  if (prefetchHandlers) {
    void prefetchHandlers.fetchUsers()
    void prefetchHandlers.fetchAgents()
    return
  }

  pendingUsersTabPrefetch = true
}

export function useUsersTab() {
  const { t } = useI18n()


const columns = computed<any[]>(() => [
  { label: t('users.avatar'), key: 'avatar', sortable: false, width: '62px', align: 'left' },
  { label: t('users.tableHeaders.username'), key: 'username', sortable: true, align: 'left', width: '135px' },
  { label: t('users.tableHeaders.id'), key: 'id', sortable: true, align: 'left', width: '155px' },
  { label: t('users.displayId'), key: 'display_id', sortable: true, align: 'left', width: '155px' },
  { label: t('users.tableHeaders.agent'), key: 'agent', sortable: true, align: 'left', width: '90px' },
  { label: t('users.tableHeaders.phone'), key: 'phone', sortable: true, align: 'left', width: '70px' },
  // { label: t('users.tableHeaders.role'), key: 'role', sortable: true, align: 'center', width: '80px' },
  { label: t('users.tableHeaders.vip'), key: 'vip', sortable: false, align: 'left', width: '55px' },
  // { label: t('users.tableHeaders.points'), key: 'points', sortable: true, align: 'center', width: '70px' },
  // { label: t('users.tableHeaders.country'), key: 'country', sortable: true, align: 'center', width: '100px' },
  { label: t('users.tableHeaders.visitor'), key: 'is_visitor', sortable: true, align: 'left', width: '72px' },
  // { label: t('users.tableHeaders.verified'), key: 'is_verified', sortable: true, align: 'center', width: '80px' },
  { label: t('users.tableHeaders.createdAt'), key: 'created_at', sortable: true, align: 'left', width: '135px' },
  { label: t('users.tableHeaders.status'), key: 'is_active', sortable: true, align: 'left', width: '78px' },
  // { label: t('users.actions') || 'Actions', key: 'actions', sortable: false, align: 'center', width: '100px' },
])

const avatarLoading = new Set<string>()
const avatarLoadQueue: string[] = []
const MAX_CONCURRENT_AVATAR_LOADS = 8
let avatarLoadsInFlight = 0

const processAvatarLoadQueue = () => {
  while (avatarLoadsInFlight < MAX_CONCURRENT_AVATAR_LOADS && avatarLoadQueue.length > 0) {
    const avatarKey = avatarLoadQueue.shift()
    if (!avatarKey || avatarLoading.has(avatarKey) || avatarUrlCache.value[avatarKey]) continue

    avatarLoading.add(avatarKey)
    avatarLoadsInFlight += 1

    CDNImage(avatarKey)
      .then((blob: Blob) => {
        avatarUrlCache.value[avatarKey] = URL.createObjectURL(blob)
      })
      .catch((error: any) => {
        console.error("Erreur lors du decryptage de l'avatar:", error)
      })
      .finally(() => {
        avatarLoading.delete(avatarKey)
        avatarLoadsInFlight -= 1
        processAvatarLoadQueue()
      })
  }
}

const getAvatarUrl = (username: string, avatar?: string): string => {
  const fallbackUrl = `https://api.dicebear.com/9.x/adventurer/svg?seed=${username}`
  const avatarKey = (avatar || '').trim()
  if (!avatarKey) return fallbackUrl

  const cached = avatarUrlCache.value[avatarKey]
  if (cached) return cached

  if (!avatarLoading.has(avatarKey) && !avatarLoadQueue.includes(avatarKey)) {
    avatarLoadQueue.push(avatarKey)
    processAvatarLoadQueue()
  }

  return fallbackUrl
}

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '-'
  return dayjs(iso).format('YYYY-MM-DD HH:mm')
}

const getVipLabel = (rowData: any) => {
  const vip = rowData?.vip
  if (vip && typeof vip === 'object') {
    // Try to get translated name from translations[0].name
    if (vip.translations && vip.translations.length > 0 && vip.translations[0].name) {
      return vip.translations[0].name
    }
    // Fallback to other VIP properties
    return vip.name || vip.title || vip.label || vip.id || 'VIP'
  }
  if (vip && typeof vip === 'string') return vip
  if (rowData?.vip_id) return 'VIP'
  return '-'
}

const getVipValidityDays = (rowData: any): number | string | null => {
  const vip = rowData?.vip
  const currentVipId = rowData?.vip_id ?? vip?.id
  const matchedPlan = currentVipId
    ? vipPlans.value.find((plan: any) => String(plan.id) === String(currentVipId))
    : null

  const validDays =
    vip?.valid_days ??
    vip?.validity_days ??
    rowData?.valid_days ??
    rowData?.duration_days ??
    matchedPlan?.valid_days ??
    matchedPlan?.validity_days

  return validDays !== undefined && validDays !== null && validDays !== '' ? validDays : null
}

const getCurrentVipCardLabel = (rowData: any): string => {
  const vipName = getVipLabel(rowData)
  if (vipName === '-') return t('users.noVip')

  const validDays = getVipValidityDays(rowData)
  return validDays !== null ? `${vipName} (${validDays} ${t('users.days')})` : vipName
}

const getVipPlanName = (vipId: number | string) => {
  const vip = vipPlans.value.find((v: any) => v.id === vipId)
  if (!vip) return `VIP ${vipId}`
  const tr = vip.translations?.find((t: any) => t.lang === 'fr-FR' || t.lang === 'en-US')
  return tr ? tr.name : `VIP ${vipId}`
}

const getVipPlanDisplayName = (vip: any): string => {
  // Try different ways to get the plan name
  if (vip.translations && vip.translations.length > 0) {
    const frenchTr = vip.translations.find((t: any) => t.lang === 'fr-FR')
    const englishTr = vip.translations.find((t: any) => t.lang === 'en-US')
    if (frenchTr?.name) return frenchTr.name
    if (englishTr?.name) return englishTr.name
    if (vip.translations[0]?.name) return vip.translations[0].name
  }

  // Fallback to direct properties
  if (vip.name) return vip.name
  if (vip.title) return vip.title
  if (vip.label) return vip.label

  // Last resort
  return `VIP ${vip.id || 'Plan'}`
}

const updatePriceFromVipPlan = () => {
  // Reserved hook to update displayed price from selected plan if needed
  // console.log('VIP plan selected:', form.value.vipId)
}

const loadVipPlansOnce = async (): Promise<any[]> => {
  if (vipPlans.value.length > 0) {
    return vipPlans.value
  }

  if (vipPlansFetchPromise) {
    return vipPlansFetchPromise
  }

  vipPlansFetchPromise = (async () => {
    try {
      const resp = await apiClient.post(api.listVips(), {})
      const payload = resp.data || {}
      vipPlans.value = payload.data || payload.items || payload || []
      return vipPlans.value
    } catch {
      vipPlans.value = []
      return vipPlans.value
    } finally {
      vipPlansFetchPromise = null
    }
  })()

  return vipPlansFetchPromise
}

const toBooleanFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true'
  }
  return false
}

const hasAnyVip = computed(() => {
  return !!vipUser.value && (
    (vipUser.value.vip !== null && vipUser.value.vip !== undefined && vipUser.value.vip !== '') ||
    (vipUser.value.vip_id !== null && vipUser.value.vip_id !== undefined && vipUser.value.vip_id !== '')
  )
})

const isVipPlanActive = (vipPlan: any): boolean => {
  const activeFlag = vipPlan?.is_active ?? vipPlan?.isActive
  // If API does not provide an activity flag, keep plan selectable.
  if (activeFlag === undefined || activeFlag === null || activeFlag === '') return true
  return toBooleanFlag(activeFlag)
}

const activeVipPlans = computed(() => vipPlans.value.filter((vip) => isVipPlanActive(vip)))
const hasActiveVipPlans = computed(() => activeVipPlans.value.length > 0)

const hasRenewableVip = computed(() => {
  if (!hasAnyVip.value) return false

  const currentVipFlag = vipUser.value?.vip?.is_active ?? vipUser.value?.vip?.isActive
  if (currentVipFlag !== undefined && currentVipFlag !== null && currentVipFlag !== '') {
    return toBooleanFlag(currentVipFlag)
  }

  const currentVipId = vipUser.value?.vip_id ?? vipUser.value?.vip?.id
  if (!currentVipId) return true
  if (vipPlans.value.length === 0) return true

  return activeVipPlans.value.some((vip) => String(vip.id) === String(currentVipId))
})

// Modal + edit state
const doShowEditUserModal = ref(false)
const userToEdit = ref<any | null>(null)

let agentsFetchPromise: Promise<void> | null = null
let cachedAgentsList: any[] | null = null
let vipPlansFetchPromise: Promise<any[]> | null = null

const isTableRefreshing = computed(() => isLoading.value && hasLoadedUsers.value)
const skeletonRowCount = computed(() => Math.min(pagination.value.perPage, 10))

const AGENT_BASE_CACHE_STORAGE_KEY = 'users_tab_agent_base_cache_v1'
const AGENT_BASE_CACHE_TTL_MS = 5 * 60 * 1000

const canUseUsersListCache = () =>
  !filters.value.agentId &&
  !(filters.value.search || '').trim() &&
  !(filters.value.id || '').trim() &&
  !(filters.value.display_id || '').trim() &&
  !(filters.value.phone || '').trim()

const canUseUsersListPageCache = () => {
  const hasFieldFilters =
    Boolean((filters.value.search || '').trim()) ||
    Boolean((filters.value.id || '').trim()) ||
    Boolean((filters.value.display_id || '').trim()) ||
    Boolean((filters.value.phone || '').trim())

  if (hasFieldFilters) return false
  if (filters.value.agentId) {
    return agentFilterUsesServerPagination.value
  }
  return true
}

const getUsersListCacheContext = () => {
  const isImpersonation =
    !!sessionStorage.getItem('impersonation_token') || !!sessionStorage.getItem('impersonation_session')
  const role = sessionStorage.getItem('role') || localStorage.getItem('role') || ''
  const agentId =
    filters.value.agentId && agentFilterUsesServerPagination.value
      ? String(filters.value.agentId).trim()
      : ''

  return { isImpersonation, role, agentId }
}

const readUsersListPageCache = (cacheKey: string): { items: any[]; total: number } | null => {
  const memoryEntry = usersListPageMemoryCache.get(cacheKey)
  if (memoryEntry && Date.now() - memoryEntry.ts <= USERS_LIST_CACHE_TTL_MS) {
    return { items: memoryEntry.items, total: memoryEntry.total }
  }

  return readUsersListCacheFromStorage(cacheKey)
}

const getUsersListPageCacheAge = (cacheKey: string): number | null => {
  const memoryEntry = usersListPageMemoryCache.get(cacheKey)
  if (memoryEntry) return Date.now() - memoryEntry.ts

  const store = readUsersListPagesCacheStore()
  const entry = store[cacheKey]
  if (entry && typeof entry.ts === 'number') return Date.now() - entry.ts

  return null
}

const shouldSkipUsersListNetworkFetch = (
  cacheKey: string,
  options: FetchUsersOptions,
): boolean => {
  const cacheAge = getUsersListPageCacheAge(cacheKey)
  if (cacheAge === null) return false
  if (options.paginationChange) return cacheAge <= USERS_LIST_CACHE_TTL_MS
  return cacheAge <= 15_000
}

const beginUsersFetchRequest = () => {
  activeUsersFetchController?.abort()
  activeUsersFetchController = new AbortController()
  return activeUsersFetchController.signal
}

const writeUsersListPageCache = (cacheKey: string, items: any[], total: number) => {
  usersListPageMemoryCache.set(cacheKey, { items, total, ts: Date.now() })
  if (usersListPageMemoryCache.size > USERS_LIST_MEMORY_CACHE_MAX_ENTRIES) {
    const oldestKey = usersListPageMemoryCache.keys().next().value
    if (oldestKey) usersListPageMemoryCache.delete(oldestKey)
  }

  writeUsersListCacheToStorage(cacheKey, items, total)
}

const applyUsersListPageCacheIfAvailable = (): boolean => {
  if (!canUseUsersListPageCache()) return false

  const params = buildListUsersRequestParams()
  const { role, isImpersonation, agentId } = getUsersListCacheContext()
  const cacheKey = buildUsersListCacheKey(params, role, isImpersonation, agentId)
  const cachedList = readUsersListPageCache(cacheKey)
  if (!cachedList) return false

  users.value = cachedList.items
  pagination.value.total = cachedList.total
  hasLoadedUsers.value = true
  isLoading.value = false
  return true
}

const persistUsersListPageCache = (cacheKey: string) => {
  if (!canUseUsersListPageCache()) return
  writeUsersListPageCache(cacheKey, users.value, pagination.value.total)
}

const prefetchAdjacentUsersListPages = () => {
  if (!canUseUsersListPageCache()) return

  const { role, isImpersonation, agentId } = getUsersListCacheContext()
  const headers = getAuthHeaders()
  const totalPages = Math.ceil(Math.max(1, pagination.value.total) / pagination.value.perPage)
  const targetPages = [
    pagination.value.page - 2,
    pagination.value.page - 1,
    pagination.value.page + 1,
    pagination.value.page + 2,
  ].filter((page) => page >= 1 && page <= totalPages)

  targetPages.forEach((page) => {
    const params = buildListUsersRequestParams()
    params.page = page
    const cacheKey = buildUsersListCacheKey(params, role, isImpersonation, agentId)
    if (readUsersListPageCache(cacheKey)) return

    const requestParams: Record<string, unknown> = { ...params }
    if (agentId) {
      requestParams.agent_id = agentId
    }

    void apiClient
      .post(api.listUsers(), requestParams, { headers })
      .then((resp) => {
        const payload = resp.data || {}
        const items = payload.data || payload.items || payload || []
        const mappedUsers = (Array.isArray(items) ? items : []).map(mapUserFromApi).filter(userMatchesAllFilters)
        const total = Number(payload.total ?? payload.count ?? mappedUsers.length)
        writeUsersListPageCache(cacheKey, mappedUsers, total)
      })
      .catch(() => {
        // Prefetch best-effort — ignore failures
      })
  })
}
const USER_STATUS_SYNC_EVENT = 'xo:user-status-sync'
const USER_STATUS_SYNC_STORAGE_KEY = 'xo:user-status-sync'

const { init: notify } = useToast()
const confirm = (useModal() as any).confirm as (options: any) => Promise<any>
const userStatusLoading = ref<Set<string>>(new Set())
let userStatusSyncRefreshTimer: ReturnType<typeof setTimeout> | null = null

type UserStatusSyncPayload = {
  userId: string
  is_active: boolean
  source?: string
  updatedAt?: number
}

const setUserStatusLoading = (userId: string | number, isLoading: boolean) => {
  const next = new Set(userStatusLoading.value)
  const key = String(userId)
  if (isLoading) next.add(key)
  else next.delete(key)
  userStatusLoading.value = next
}

const getAuthHeaders = () => {
  const token =
    sessionStorage.getItem('impersonation_token') ||
    sessionStorage.getItem('admin_token') ||
    localStorage.getItem('admin_token')

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

const clearUsersTabCaches = () => {
  agentBaseCache.value = null
  agentFilterCache.value = null
  agentFilterUsesServerPagination.value = false
  usersListPageMemoryCache.clear()
  try {
    sessionStorage.removeItem(AGENT_BASE_CACHE_STORAGE_KEY)
    sessionStorage.removeItem(USERS_LIST_CACHE_STORAGE_KEY)
  } catch {
    // Ignore storage failures
  }
}

const patchUserStatusOnList = (list: any[] | null | undefined, userId: string, isActive: boolean): any[] => {
  if (!Array.isArray(list) || list.length === 0) return Array.isArray(list) ? list : []
  return list.map((entry: any) => {
    if (String(entry?.id ?? '') !== userId) return entry
    if (Boolean(entry?.is_active) === isActive) return entry
    return { ...entry, is_active: isActive }
  })
}

const applyUserStatusLocally = (userId: string, isActive: boolean) => {
  users.value = patchUserStatusOnList(users.value, userId, isActive)

  if (agentBaseCache.value) {
    agentBaseCache.value = {
      ...agentBaseCache.value,
      items: patchUserStatusOnList(agentBaseCache.value.items, userId, isActive),
    }
  }

  if (agentFilterCache.value) {
    agentFilterCache.value = {
      ...agentFilterCache.value,
      items: patchUserStatusOnList(agentFilterCache.value.items, userId, isActive),
    }
  }
}

const scheduleUsersRefreshFromSync = () => {
  if (userStatusSyncRefreshTimer) {
    clearTimeout(userStatusSyncRefreshTimer)
  }
  userStatusSyncRefreshTimer = setTimeout(() => {
    userStatusSyncRefreshTimer = null
    clearUsersTabCaches()
    fetchVipUsers()
  }, 180)
}

const handleIncomingUserStatusSync = (payload: UserStatusSyncPayload | null | undefined) => {
  if (!payload?.userId) return
  if (payload.source === 'users_tab') return
  const userId = String(payload.userId)
  const isActive = Boolean(payload.is_active)

  applyUserStatusLocally(userId, isActive)
  scheduleUsersRefreshFromSync()
}

const onUserStatusSyncEvent = (event: Event) => {
  const detail = (event as CustomEvent<UserStatusSyncPayload>).detail
  handleIncomingUserStatusSync(detail)
}

const onUserStatusSyncStorage = (event: StorageEvent) => {
  if (event.key !== USER_STATUS_SYNC_STORAGE_KEY || !event.newValue) return
  try {
    const payload = JSON.parse(event.newValue) as UserStatusSyncPayload
    handleIncomingUserStatusSync(payload)
  } catch {
    // Ignore malformed payload
  }
}

const broadcastUserStatusSync = (userId: string, isActive: boolean) => {
  const payload: UserStatusSyncPayload = {
    userId: String(userId),
    is_active: isActive,
    source: 'users_tab',
    updatedAt: Date.now(),
  }

  try {
    localStorage.setItem(USER_STATUS_SYNC_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore storage failures
  }

  window.dispatchEvent(new CustomEvent(USER_STATUS_SYNC_EVENT, { detail: payload }))
}

watchEffect(() => {
  if (error.value) {
    notify({ message: error.value.message || String(error.value), color: 'danger' })
  }
})

onBeforeUnmount(() => {
  window.removeEventListener(USER_STATUS_SYNC_EVENT, onUserStatusSyncEvent as EventListener)
  window.removeEventListener('storage', onUserStatusSyncStorage)
  if (userStatusSyncRefreshTimer) {
    clearTimeout(userStatusSyncRefreshTimer)
    userStatusSyncRefreshTimer = null
  }
  Object.values(avatarUrlCache.value).forEach((url) => {
    URL.revokeObjectURL(url)
  })
})

const preloadAgentNames = async (usersList: any[]) => {
  // console.log('Preloading agent names for', usersList.length, 'users')
}

const fetchAgents = async () => {
  if (cachedAgentsList && cachedAgentsList.length > 0) {
    agents.value = cachedAgentsList
    return
  }

  if (agentsFetchPromise) {
    return agentsFetchPromise
  }

  agentsFetchPromise = (async () => {
    try {
      const headers = getAuthHeaders()
      const resp = await apiClient.post(api.listAgents?.() || '/agents/list', { page: 1, limit: 100 }, { headers })
      const payload = resp.data || {}
      const items = payload.data || payload.items || payload || []

      agents.value = items.map((agent: any) => ({
        id: agent.id,
        username: agent.username || agent.email || agent.name || `Agent ${agent.id}`,
        email: agent.email,
        name: agent.name,
      }))
      cachedAgentsList = agents.value
    } catch (e) {
      console.error('Error fetching agents', e)
      agents.value = []
    } finally {
      agentsFetchPromise = null
    }
  })()

  return agentsFetchPromise
}

const normalizeBooleanFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true'
  }
  return false
}

const mapUserFromApi = (u: any) => ({
  id: u.id,
  display_id: u.display_id ?? null,
  username: u.username || u.loginId || u.nickname || '',
  nickname: u.nickname || '',
  phone: u.phone || null,
  avatar: u.avatar || '',
  loginId: u.loginId || '',
  role: u.role || 'user',
  vip_id: u.vip_id ?? null,
  vip: u.vip ?? null,
  is_active: u.is_active ?? true,
  points: u.points || 0,
  country: u.country || null,
  agent: u.agent || null,
  agent_id: u.agent_id ?? u.agentId ?? null,
  is_visitor: normalizeBooleanFlag(u.fromApp ?? u.from_app ?? u.is_visitor),
  is_verified: u.is_verified ?? false,
  created_at: u.created_at || null,
  _vip: null,
})

const applyLocalPagination = (items: any[]) => {
  pagination.value.total = items.length
  const start = (pagination.value.page - 1) * pagination.value.perPage
  users.value = items.slice(start, start + pagination.value.perPage)
}

const buildListUsersRequestParams = (overrides: Record<string, unknown> = {}) => {
  const params: any = {
    page: pagination.value.page,
    pageSize: pagination.value.perPage,
    limit: pagination.value.perPage,
    keyword: filters.value.search || undefined,
    ...overrides,
  }

  if (filters.value.id) {
    params.id = filters.value.id.trim()
  }

  if (filters.value.display_id) {
    params.display_id = filters.value.display_id.trim()
  }

  if (filters.value.phone) {
    params.phone = filters.value.phone.trim()
  }

  if (filters.value.search) params.search = filters.value.search
  if (filters.value.isActive !== undefined) params.status = filters.value.isActive ? 'active' : 'expired'

  return params
}

const buildAgentBaseCacheKey = (params: any, role: string, isImpersonation: boolean) =>
  JSON.stringify({
    keyword: params.keyword || '',
    search: params.search || '',
    id: params.id || '',
    display_id: params.display_id || '',
    phone: params.phone || '',
    status: params.status || '',
    role,
    isImpersonation,
  })

const buildCurrentAgentFilteredCacheKey = () => {
  const isImpersonation =
    !!sessionStorage.getItem('impersonation_token') || !!sessionStorage.getItem('impersonation_session')
  const role = sessionStorage.getItem('role') || localStorage.getItem('role') || ''
  const selectedAgentId = filters.value.agentId != null ? String(filters.value.agentId).trim() : ''
  if (!selectedAgentId) return ''

  const params: any = {
    keyword: filters.value.search || undefined,
  }
  if (filters.value.id) params.id = filters.value.id.trim()
  if (filters.value.display_id) params.display_id = filters.value.display_id.trim()
  if (filters.value.phone) params.phone = filters.value.phone.trim()
  if (filters.value.search) params.search = filters.value.search
  if (filters.value.isActive !== undefined) params.status = filters.value.isActive ? 'active' : 'expired'

  const baseCacheKey = buildAgentBaseCacheKey(params, role, isImpersonation)
  return `${baseCacheKey}::${selectedAgentId}`
}

const readAgentBaseCacheFromStorage = (cacheKey: string): any[] | null => {
  try {
    const raw = sessionStorage.getItem(AGENT_BASE_CACHE_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as { key?: string; ts?: number; items?: any[] }
    if (!parsed?.key || !Array.isArray(parsed.items) || typeof parsed.ts !== 'number') return null
    if (parsed.key !== cacheKey) return null
    if (Date.now() - parsed.ts > AGENT_BASE_CACHE_TTL_MS) return null
    return parsed.items
  } catch {
    return null
  }
}

const writeAgentBaseCacheToStorage = (cacheKey: string, items: any[]) => {
  try {
    const payload = JSON.stringify({ key: cacheKey, ts: Date.now(), items })
    sessionStorage.setItem(AGENT_BASE_CACHE_STORAGE_KEY, payload)
  } catch {
    // Ignore storage quota errors
  }
}

const userMatchesAgentFilter = (user: any): boolean => {
  if (!filters.value.agentId) return true

  const selectedAgentId = String(filters.value.agentId).trim()
  const selectedAgent = agents.value.find((agent) => String(agent.id) === selectedAgentId)
  const selectedAgentName = (selectedAgent?.username || '').toString().trim().toLowerCase()

  const nestedAgentId = user?.agent?.id != null ? String(user.agent.id).trim() : ''
  const topLevelAgentId = user?.agent_id != null ? String(user.agent_id).trim() : ''
  const nestedAgentName = (user?.agent?.username || '').toString().trim().toLowerCase()
  const hasNestedAgent = Boolean(nestedAgentId || nestedAgentName)

  // Keep filtering aligned with displayed "agent.username":
  // if nested agent exists, trust it first and ignore conflicting top-level agent_id.
  if (hasNestedAgent) {
    if (nestedAgentId && nestedAgentId === selectedAgentId) return true
    if (selectedAgentName && nestedAgentName === selectedAgentName) return true
    return false
  }

  if (topLevelAgentId === selectedAgentId) return true

  return false
}

const userMatchesFieldFilters = (_user: any): boolean => {
  // Field filtering (id/display_id/phone) is handled by backend.
  // Avoid local re-filtering to prevent hiding valid API results.
  return true
}

const userMatchesAllFilters = (user: any): boolean => {
  return userMatchesAgentFilter(user) && userMatchesFieldFilters(user)
}

const applyUsersListTotal = (payload: any, mappedCount: number) => {
  const hasFieldFilters =
    Boolean((filters.value.id || '').toString().trim()) ||
    Boolean((filters.value.display_id || '').toString().trim()) ||
    Boolean((filters.value.phone || '').toString().trim())

  if (hasFieldFilters || (filters.value.agentId && !agentFilterUsesServerPagination.value)) {
    pagination.value.total = mappedCount
  } else {
    pagination.value.total = payload.total ?? payload.count ?? mappedCount
  }
}

const applyUsersListResponse = (payload: any, rawItems: any[]) => {
  const items = Array.isArray(rawItems) ? rawItems : []
  const mappedUsers = items.map(mapUserFromApi).filter(userMatchesAllFilters)
  users.value = mappedUsers
  applyUsersListTotal(payload, mappedUsers.length)
  return mappedUsers
}

const fetchAllUsersForAgentFilter = async (url: string, baseParams: any, headers: Record<string, string>) => {
  const aggregated: any[] = []
  const batchSize = 1000
  const concurrency = 6

  const firstResp = await apiClient.post(url, { ...baseParams, page: 1, pageSize: batchSize, limit: batchSize }, { headers })
  const firstPayload = firstResp.data || {}
  const firstItems = firstPayload.data || firstPayload.items || firstPayload || []
  const firstPageItems = Array.isArray(firstItems) ? firstItems : []
  aggregated.push(...firstPageItems)

  const totalPagesFromPayload = Number(firstPayload.totalPages || 0)
  const totalCountFromPayload = Number(firstPayload.total || firstPayload.total_count || 0)
  const inferredTotalPages = totalCountFromPayload > 0 ? Math.ceil(totalCountFromPayload / batchSize) : 1
  const totalPages = totalPagesFromPayload > 0 ? totalPagesFromPayload : inferredTotalPages
  if (totalPages <= 1 || firstPageItems.length === 0) return aggregated

  for (let startPage = 2; startPage <= totalPages; startPage += concurrency) {
    const pages: number[] = []
    for (let p = startPage; p < startPage + concurrency && p <= totalPages; p += 1) {
      pages.push(p)
    }

    const responses = await Promise.all(
      pages.map((page) => apiClient.post(url, { ...baseParams, page, pageSize: batchSize, limit: batchSize }, { headers })),
    )

    responses.forEach((resp) => {
      const payload = resp.data || {}
      const items = payload.data || payload.items || payload || []
      if (Array.isArray(items) && items.length > 0) {
        aggregated.push(...items)
      }
    })
  }

  return aggregated
}

const fetchUsersByAgentFromBackend = async (
  url: string,
  baseParams: any,
  headers: Record<string, string>,
  agentId: string,
) => {
  return fetchAllUsersForAgentFilter(url, { ...baseParams, agent_id: agentId }, headers)
}

const fetchVipUsersInternal = async (options: FetchUsersOptions = {}) => {
  const currentSequence = ++fetchSequence.value
  const isLatestRequest = () => currentSequence === fetchSequence.value

  if (!hasLoadedUsers.value) {
    isLoading.value = true
  }
  try {
    const isImpersonation =
      !!sessionStorage.getItem('impersonation_token') || !!sessionStorage.getItem('impersonation_session')
    const role = sessionStorage.getItem('role') || localStorage.getItem('role') || ''

    if (filters.value.search && (isImpersonation || role === 'agent')) {
      const headers = getAuthHeaders()
      // Use searchUser for agents too (backend enforces role scope)
      const resp = await apiClient.post(api.searchUser(filters.value.search), {}, { headers })
      const results = resp.data || []

      const term = filters.value.search.toLowerCase()
      const filteredResults = results.filter((u: any) => {
        const uname = (u.username || '').toString().toLowerCase()
        const login = (u.loginId || '').toString().toLowerCase()
        const nick = (u.nickname || '').toString().toLowerCase()
        const idStr = u.id?.toString() || ''
        return uname.includes(term) || login.includes(term) || nick.includes(term) || idStr.includes(term)
      })

      if (!isLatestRequest()) return
      const mapped = filteredResults.map(mapUserFromApi).filter(userMatchesAllFilters)
      applyLocalPagination(mapped)
      return
    }

    const params = buildListUsersRequestParams()
    const { agentId } = getUsersListCacheContext()
    const usersListCacheKey = buildUsersListCacheKey(params, role, isImpersonation, agentId)

    if (canUseUsersListPageCache()) {
      const cachedList = readUsersListPageCache(usersListCacheKey)
      if (cachedList) {
        users.value = cachedList.items
        pagination.value.total = cachedList.total
        hasLoadedUsers.value = true
        isLoading.value = false

        if (shouldSkipUsersListNetworkFetch(usersListCacheKey, options)) {
          prefetchAdjacentUsersListPages()
          return
        }
      }
    }

    // Utiliser searchUser seulement s'il y a une recherche, sinon listUsers
    let url = api.listUsers()
    if ((isImpersonation || role === 'agent') && filters.value.search) {
      url = api.searchUser(filters.value.search)
    }

    const headers = getAuthHeaders()
    const fetchSignal = beginUsersFetchRequest()
    const requestConfig = (requestHeaders: Record<string, string> = headers) => ({
      headers: requestHeaders,
      signal: fetchSignal,
    })

    // Agent filter is applied client-side on an aggregated dataset
    // because API data may be inconsistent between `agent_id` and `agent.id`.
    if (filters.value.agentId) {
      const baseCacheKey = buildAgentBaseCacheKey(params, role, isImpersonation)
      const selectedAgentId = String(filters.value.agentId).trim()
      const filteredCacheKey = `${baseCacheKey}::${selectedAgentId}`

      // Pagination serveur via agent_id — 1 requête au lieu de l'agrégation multi-pages quand l'API répond
      if (agentFilterUsesServerPagination.value) {
        const serverResp = await apiClient.post(
          api.listUsers(),
          { ...params, agent_id: selectedAgentId },
          requestConfig(),
        )
        if (!isLatestRequest()) return
        const serverPayload = serverResp.data || {}
        const serverItems = serverPayload.data || serverPayload.items || serverPayload || []
        const serverMappedUsers = applyUsersListResponse(serverPayload, serverItems)
        const serverTotal = Number(serverPayload.total ?? serverPayload.count ?? 0)
        if (serverMappedUsers.length > 0 || serverTotal > 0) {
          persistUsersListPageCache(usersListCacheKey)
          prefetchAdjacentUsersListPages()
          return
        }
        // Serveur sans résultat — repasser en mode agrégation client (logique historique)
        agentFilterUsesServerPagination.value = false
      }

      if (agentFilterCache.value?.key === filteredCacheKey) {
        applyLocalPagination(agentFilterCache.value.items)
        return
      }

      let baseUsers: any[]
      if (agentBaseCache.value?.key === baseCacheKey) {
        baseUsers = agentBaseCache.value.items
      } else {
        const persistedCache = readAgentBaseCacheFromStorage(baseCacheKey)
        if (persistedCache) {
          baseUsers = persistedCache
          agentBaseCache.value = { key: baseCacheKey, items: baseUsers }
        } else {
          // Essai rapide : pagination serveur avant fetchAllUsersForAgentFilter (agrégation)
          const serverTryResp = await apiClient.post(
            api.listUsers(),
            { ...params, agent_id: selectedAgentId },
            requestConfig(),
          )
          if (!isLatestRequest()) return
          const serverTryPayload = serverTryResp.data || {}
          const serverTryItems = serverTryPayload.data || serverTryPayload.items || serverTryPayload || []
          const serverTryMappedUsers = serverTryItems.map(mapUserFromApi).filter(userMatchesAllFilters)
          const serverTryTotal = Number(serverTryPayload.total ?? serverTryPayload.count ?? 0)
          if (serverTryMappedUsers.length > 0 || serverTryTotal > 0) {
            agentFilterUsesServerPagination.value = true
            applyUsersListResponse(serverTryPayload, serverTryItems)
            persistUsersListPageCache(usersListCacheKey)
            prefetchAdjacentUsersListPages()
            return
          }

          const quickAgentItems = await fetchUsersByAgentFromBackend(api.listUsers(), params, headers, selectedAgentId)
          if (!isLatestRequest()) return
          const quickMappedUsers = quickAgentItems.map(mapUserFromApi).filter(userMatchesAllFilters)
          if (quickMappedUsers.length > 0) {
            agentFilterCache.value = { key: filteredCacheKey, items: quickMappedUsers }
            applyLocalPagination(quickMappedUsers)
            return
          }

          const aggregatedItems = await fetchAllUsersForAgentFilter(api.listUsers(), params, headers)
          if (!isLatestRequest()) return
          baseUsers = aggregatedItems.map(mapUserFromApi)
          agentBaseCache.value = { key: baseCacheKey, items: baseUsers }
          writeAgentBaseCacheToStorage(baseCacheKey, baseUsers)
        }
      }

      if (!isLatestRequest()) return
      const mappedUsers = baseUsers.filter(userMatchesAllFilters)
      agentFilterCache.value = { key: filteredCacheKey, items: mappedUsers }
      applyLocalPagination(mappedUsers)
      return
    }

    if (agentFilterCache.value) {
      agentFilterCache.value = null
    }
    agentFilterUsesServerPagination.value = false

    let resp
    if (filters.value.search && (isImpersonation || role === 'agent')) {
      resp = await apiClient.post(url, {}, requestConfig())
    } else {
      resp = await apiClient.post(url, params, requestConfig())
    }

    const payload = resp.data || {}
    const items = payload.data || payload.items || payload || []

    if (!isLatestRequest()) return
    applyUsersListResponse(payload, items)

    persistUsersListPageCache(usersListCacheKey)
    prefetchAdjacentUsersListPages()

  } catch (e) {
    if ((e as { code?: string; name?: string })?.code === 'ERR_CANCELED' || (e as { name?: string })?.name === 'CanceledError') {
      return
    }
    console.error('Error fetching users', e)
    error.value = e
  } finally {
    if (isLatestRequest()) {
      isLoading.value = false
      hasLoadedUsers.value = true
    }
  }
}

const runFetchVipUsers = async (options: FetchUsersOptions = {}) => {
  if (!options.paginationChange && fetchVipUsersPromise) {
    return fetchVipUsersPromise
  }

  const execute = async () => {
    await fetchVipUsersInternal(options)
  }

  if (options.paginationChange) {
    return execute()
  }

  fetchVipUsersPromise = execute().finally(() => {
    fetchVipUsersPromise = null
  })

  return fetchVipUsersPromise
}

const fetchVipUsers = (options: FetchUsersOptions = {}) => runFetchVipUsers(options)

prefetchHandlers = {
  fetchUsers: () => runFetchVipUsers(),
  fetchAgents: () => fetchAgents(),
}

if (pendingUsersTabPrefetch) {
  pendingUsersTabPrefetch = false
  void runFetchVipUsers()
  void fetchAgents()
}

watch(() => filters.value.isActive, () => {
  agentFilterUsesServerPagination.value = false
  if (pagination.value.page !== 1) {
    pagination.value.page = 1
    return
  }
  fetchVipUsers()
})

watch(() => filters.value.agentId, () => {
  agentFilterUsesServerPagination.value = false
  agentFilterCache.value = null
})

watch([() => pagination.value.page, () => pagination.value.perPage], () => {
  if (filters.value.agentId && agentFilterUsesServerPagination.value) {
    void fetchVipUsers({ paginationChange: true })
    return
  }
  if (filters.value.agentId && agentFilterCache.value) {
    const expectedCacheKey = buildCurrentAgentFilteredCacheKey()
    if (agentFilterCache.value.key === expectedCacheKey) {
      applyLocalPagination(agentFilterCache.value.items)
      return
    }
  }
  void fetchVipUsers({ paginationChange: true })
})

onMounted(() => {
  window.addEventListener(USER_STATUS_SYNC_EVENT, onUserStatusSyncEvent as EventListener)
  window.addEventListener('storage', onUserStatusSyncStorage)
  // Tableau en priorité — liste agents pour le filtre en différé (dropdown non bloquant)
  if (hasLoadedUsers.value && users.value.length > 0) {
    void fetchVipUsers({ paginationChange: true })
  } else {
    fetchVipUsers()
  }
  void fetchAgents()
})

const showEditUserModal = (user: any) => {
  userToEdit.value = user
  doShowEditUserModal.value = true
}

const showAddUserModal = () => {
  userToEdit.value = null
  doShowEditUserModal.value = true
}

const onSearch = () => {
  // Reset to first page and fetch using current filters.search / filters.id
  agentFilterUsesServerPagination.value = false
  agentFilterCache.value = null
  if (pagination.value.page !== 1) {
    pagination.value.page = 1
    return
  }
  fetchVipUsers()
}

const showPasswordModal = ref(false)
const passwordModalRef = ref<any>(null)
const passwordUser = ref<any | null>(null)
const newPassword = ref('')
const confirmPassword = ref('')

// VIP modal state
const showVipModal = ref(false)
const vipUser = ref<any | null>(null)
const vipPlans = ref<any[]>([])
const selectedVip = ref<string | null>(null)
const vipDuration = ref<string | number | null>(null)
const vipUnit = ref<string | null>(null)
const vipLoading = ref(false)
const form = ref({
  vipId: '',
})

const openVipModal = async (user: any) => {
  vipUser.value = user
  selectedVip.value = null
  vipDuration.value = null
  vipUnit.value = null
  form.value.vipId = ''
  showVipModal.value = true

  // try to load vip plans (best-effort) — cache session pour éviter un refetch à chaque ouverture
  try {
    await loadVipPlansOnce()
  } catch {
    // Ignore and keep empty options
    vipPlans.value = []
  }
}

const submitButtonText = computed(() => {
  return hasRenewableVip.value ? t('users.updateVip') : t('users.createVip')
})

const saveVipForUser = async () => {
  if (!vipUser.value) return

  // If user has no VIP, use creation flow
  if (!hasRenewableVip.value) {
    if (!form.value.vipId) {
      notify({
        message: t('users.selectVipPlan'),
        color: 'warning',
      })
      return
    }
    const selectedVipPlan = activeVipPlans.value.find((v: any) => String(v.id) === String(form.value.vipId))
    if (!selectedVipPlan) {
      notify({ message: 'Plan VIP invalide ou inactif', color: 'danger' })
      return
    }

    const vipId = Number(form.value.vipId)
    if (!Number.isFinite(vipId) || vipId <= 0) {
      notify({ message: 'Identifiant VIP invalide', color: 'danger' })
      return
    }

    try {
      vipLoading.value = true

      const token =
        sessionStorage.getItem('impersonation_token') ||
        sessionStorage.getItem('admin_token') ||
        localStorage.getItem('admin_token')

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'accept': '*/*',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // VIP creation payload using selected plan
      const payload = {
        user_id: String(vipUser.value.id),
        vip_id: vipId,
        validity_type: selectedVip.value || 'unlimited',
        duration_value: vipDuration.value ? Number(vipDuration.value) : undefined,
        duration_type: vipUnit.value || 'day',
      }

      await apiClient.post(
        '/user-vips/create',
        payload,
        { headers }
      )

      notify({
        message: t('users.vipCreated', { username: vipUser.value.username }),
        color: 'success',
      })

      showVipModal.value = false
      await fetchVipUsers()
      // Refresh page after creation
      setTimeout(() => {
        window.location.reload()
      }, 1000)

    } catch (e: any) {
      const backendMessage = e?.response?.data?.message
      const isVipInactive404 =
        e?.response?.status === 404 &&
        typeof backendMessage === 'string' &&
        backendMessage.toLowerCase().includes('vip not found or inactive')

      notify({
        message: isVipInactive404
          ? 'Le VIP sélectionné est introuvable ou inactif. Choisissez un plan VIP actif.'
          : backendMessage || e?.message || t('users.vipCreationError'),
        color: 'danger',
      })
    } finally {
      vipLoading.value = false
    }
    return
  }

  // User has VIP - check if admin wants to change the plan via dropdown
  if (form.value.vipId) {
    const selectedVipPlan = activeVipPlans.value.find((v: any) => String(v.id) === String(form.value.vipId))
    if (!selectedVipPlan) {
      notify({ message: 'Plan VIP invalide ou inactif', color: 'danger' })
      return
    }

    const vipId = Number(form.value.vipId)
    if (!Number.isFinite(vipId) || vipId <= 0) {
      notify({ message: 'Identifiant VIP invalide', color: 'danger' })
      return
    }

    try {
      vipLoading.value = true

      const token =
        sessionStorage.getItem('impersonation_token') ||
        sessionStorage.getItem('admin_token') ||
        localStorage.getItem('admin_token')

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'accept': '*/*',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // To change VIP plan: delete old user-vip and create new one
      // First, try to delete the existing user-vip
      try {
        const userVipId = vipUser.value?.user_vip_id || vipUser.value?.vip?.id
        if (userVipId) {
          await apiClient.post(
            `/user-vips/${userVipId}/delete`,
            {},
            { headers }
          )
        }
      } catch (deleteError) {
        // Ignore delete error, proceed with creation
        console.warn('Failed to delete old user-vip:', deleteError)
      }

      // Then create new user-vip with the new plan
      const payload = {
        user_id: String(vipUser.value.id),
        vip_id: vipId,
        validity_type: 'unlimited',
      }

      await apiClient.post(
        '/user-vips/create',
        payload,
        { headers }
      )

      notify({
        message: t('users.vipRenewed', { username: vipUser.value.username }),
        color: 'success',
      })

      showVipModal.value = false

      // Update local user data immediately to reflect the change
      const selectedVipPlan = activeVipPlans.value.find((v: any) => String(v.id) === String(form.value.vipId))
      if (selectedVipPlan && vipUser.value) {
        // Update the user in the local users array
        const userIndex = users.value.findIndex((u: any) => String(u.id) === String(vipUser.value.id))
        if (userIndex !== -1) {
          users.value[userIndex] = {
            ...users.value[userIndex],
            vip_id: vipId,
            vip: selectedVipPlan,
          }
        }
      }

      // Clear cache and refresh
      clearUsersTabCaches()
      await fetchVipUsers()
      // Force page reload to ensure VIP data is updated


      return
    } catch (e: any) {
      const backendMessage = e?.response?.data?.message
      notify({
        message: backendMessage || e?.message || t('users.vipRenewalError'),
        color: 'danger',
      })
      return
    } finally {
      vipLoading.value = false
    }
  }

  // Logique existante pour le renouvellement de VIP
  if (!selectedVip.value) {
    notify({
      message: t('users.selectValidityType'),
      color: 'warning',
    })
    return
  }

  if (
    selectedVip.value !== 'unlimited' &&
    selectedVip.value !== 'lifetime'
  ) {
    if (!vipDuration.value || !vipUnit.value) {
      notify({
        message: t('users.fillDurationUnit'),
        color: 'warning',
      })
      return
    }
  }

  try {
    vipLoading.value = true

    const token =
      sessionStorage.getItem('impersonation_token') ||
      sessionStorage.getItem('admin_token') ||
      localStorage.getItem('admin_token')

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'accept': '*/*',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const payload: {
      user_id: string
      validity_type: 'unlimited' | 'lifetime' | 'year' | 'month' | 'day'
      duration_value?: number
      duration_type?: 'day' | 'hour' | 'minutes'
    } = {
      user_id: String(vipUser.value.id),
      validity_type: selectedVip.value as 'unlimited' | 'lifetime' | 'year' | 'month' | 'day',
    }

    if (
      selectedVip.value !== 'unlimited' &&
      selectedVip.value !== 'lifetime'
    ) {
      payload.duration_value = Number(vipDuration.value)
      payload.duration_type = vipUnit.value as 'day' | 'hour' | 'minutes'
    }

    await apiClient.post(
      '/user-vips/renew',
      payload,
      { headers }
    )

    notify({
      message: t('users.vipRenewed', { username: vipUser.value.username }),
      color: 'success',
    })

    showVipModal.value = false
    await fetchVipUsers()
    // Refresh page after update
    setTimeout(() => {
      window.location.reload()
    }, 1000)

  } catch (e: any) {
    const backendMessage = e?.response?.data?.message
    const isVipInactive404 =
      e?.response?.status === 404 &&
      typeof backendMessage === 'string' &&
      backendMessage.toLowerCase().includes('vip not found or inactive')

    notify({
      message: isVipInactive404
        ? 'Le VIP actuel est introuvable ou inactif. Sélectionnez un plan actif puis recréez le VIP.'
        : backendMessage || e?.message || t('users.vipRenewalError'),
      color: 'danger',
    })
  } finally {
    vipLoading.value = false
  }
}

const openPasswordModal = (user: any) => {
  passwordUser.value = user
  newPassword.value = ''
  confirmPassword.value = ''
  showPasswordModal.value = true
}

const showPasswordModalFn = (user: any) => {
  passwordModalRef.value?.open(user)
}

const updateUserActiveStatus = async (
  user: any,
  nextIsActive: boolean,
  options?: { confirmChange?: boolean; refreshAfter?: boolean },
) => {
  const confirmChange = options?.confirmChange ?? true
  const refreshAfter = options?.refreshAfter ?? true
  if (!user?.id) return false

  const userId = String(user.id)
  if (userStatusLoading.value.has(userId)) return false

  try {
    if (confirmChange) {
      const confirmMessage = nextIsActive
        ? `Are you sure you want to activate ${user.username}?`
        : t('users.confirmBlockUser', { username: user.username })

      const agreed = await confirm({
        maxWidth: '380px',
        message: confirmMessage,
        size: 'small',
      })
      if (!agreed) return false
    }

    setUserStatusLoading(userId, true)
    user.is_active = nextIsActive

    await apiClient.post(
      api.blockUsers(userId),
      {
        is_active: nextIsActive,
        status: nextIsActive ? 'active' : 'inactive',
      },
      { headers: getAuthHeaders() },
    )

    clearUsersTabCaches()
    broadcastUserStatusSync(userId, nextIsActive)

    notify({
      message: nextIsActive
        ? `${user.username} activated successfully`
        : `${user.username} blocked successfully`,
      color: 'success',
    })

    if (refreshAfter) {
      await fetchVipUsers()
    }

    return true
  } catch (e: any) {
    user.is_active = !nextIsActive
    notify({
      message: e?.response?.data?.message || e?.message || 'Failed to update user status',
      color: 'danger',
    })
    return false
  } finally {
    setUserStatusLoading(userId, false)
  }
}

const onToggleUserActive = async (user: any, modelValue: boolean) => {
  const nextIsActive = Boolean(modelValue)
  if (Boolean(user?.is_active) === nextIsActive) return
  await updateUserActiveStatus(user, nextIsActive, { confirmChange: true, refreshAfter: true })
}

const blockUserFn = async (user: any) => {
  const nextIsActive = !Boolean(user?.is_active)
  await updateUserActiveStatus(user, nextIsActive, { confirmChange: true, refreshAfter: true })
}

const onUserSaved = async (user: any) => {
  if (!userToEdit.value) {
    try {
      await apiClient.post(api.createAdmin(), {
        username: user.username,
        password: user.password,
        role: user.role,
        avatar: user.avatar,
      })
      notify({ message: t('users.userCreated', { username: user.username }), color: 'success' })
    } catch (e: any) {
      if (e?.response?.status === 409) {
        notify({ message: t('users.usernameExists', { username: user.username }), color: 'warning' })
      } else {
        notify({
          message: t('users.createError', {
            username: user.username,
            error: e?.response?.data?.message || e?.message || t('users.unknownError'),
          }),
          color: 'danger',
        })
      }
    } finally {
      fetchVipUsers()
    }
    return
  }

  try {
    if (user.password) {
      await apiClient.post(api.updateUserPassword(user.id), { password: user.password, confirmPassword: user.password })
    }
    notify({ message: t('users.userUpdated', { username: user.username }), color: 'success' })
  } catch (e: any) {
    notify({
      message: t('users.createError', {
        username: user.username,
        error: e?.response?.data?.message || e?.message || t('users.unknownError'),
      }),
      color: 'danger',
    })
  } finally {
    fetchVipUsers()
  }
}

const onUserDelete = async (user: any) => {
  await confirm({ message: t('users.deleteNotAvailable'), size: 'small' })
}

const editFormRef = ref()
const beforeEditFormModalClose = async (hide: () => unknown) => {
  if (editFormRef.value?.isFormHasUnsavedChanges) {
    const agreed = await confirm({
      maxWidth: '380px',
      message: t('users.unsavedChanges'),
      size: 'small',
    })
    if (agreed) hide()
  } else {
    hide()
  }
}

const totalUsers = computed(() => users.value.length)
const totalActiveUsers = computed(() => users.value.filter((u: any) => u.is_active).length)
  return {
    t,
    columns,
    getAvatarUrl,
    formatDateTime,
    getVipLabel,
    getCurrentVipCardLabel,
    getVipPlanDisplayName,
    updatePriceFromVipPlan,
    isVipPlanActive,
    hasRenewableVip,
    doShowEditUserModal,
    userToEdit,
    users,
    isLoading,
    hasLoadedUsers,
    isTableRefreshing,
    skeletonRowCount,
    filters,
    sorting,
    pagination,
    agents,
    userStatusLoading,
    showVipModal,
    vipUser,
    vipPlans,
    form,
    vipLoading,
    submitButtonText,
    passwordModalRef,
    editFormRef,
    totalUsers,
    onSearch,
    showPasswordModalFn,
    openVipModal,
    blockUserFn,
    onToggleUserActive,
    saveVipForUser,
    onUserSaved,
    beforeEditFormModalClose,
  }
}
